// @ts-strict-ignore
import { type SyntheticEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Spacer } from "@aviary";
import { CustomDisclaimer } from "@shared/components/CustomDisclaimer/CustomDisclaimer";
import { isEmailValid } from "@shared/components/EmailInput/EmailValidation/EmailValidation";
import { LoginOptions } from "@shared/components/LoginOptions/LoginOptions";
import { useHistory } from "@shared/context/HistoryContext";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "@shared/react-router-dom/react-router-dom";
import { SwiftFillSteps } from "@shared/types/SwiftFillSteps";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { RecaptchaErrorBox } from "@unauthenticated/shared/components/RecaptchaErrorBox/RecaptchaErrorBox";
import { SignInEmailInput } from "@unauthenticated/shared/components/SignInForm/SignInEmailInput";
import { SignInWrapper } from "@unauthenticated/shared/components/SignInWrapper/SignInWrapper";
import { SSOErrorBox } from "@unauthenticated/shared/components/SSOErrorBox/SSOErrorBox";
import { useGlobalConfig } from "@unauthenticated/shared/context/GlobalConfig/GlobalConfig";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import type { PatientAttributes } from "@unauthenticated/shared/data/types/PatientAttributes.d";
import { l } from "@unauthenticated/shared/locales/i18n";
import { PreviewModeBanner } from "@unauthenticated/signupSignin/components/PreviewModeBanner/PreviewModeBanner";
import { ValuePropsCard } from "@unauthenticated/signupSignin/components/ValuePropsCard/ValuePropsCard";
import type { LocationType } from "@unauthenticated/signupSignin/data/types/PatientAuth";

import { usePatientNextLoginStepUnauthenticatedSignUpSignInMutation } from "./PatientNextLoginStep.mutation.generated";

import * as styles from "./EmailCheckForm.styles";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  ssoErrors?: string;
  storeId?: string;
  isPreview: boolean;
  isStorefronts?: boolean;
  customAuthDisclaimer?: string | null;
  isFullWidthButtons?: boolean;
}

const EmailCheckForm = ({
  patientAttributes,
  ssoErrors,
  storeId,
  isPreview,
  additionalAttributes,
  isStorefronts,
  customAuthDisclaimer,
  isFullWidthButtons,
}: Props) => {
  const SIGN_IN_PAGE = "rx_patient_sign_in_page";
  const PAGE_VERSION = "react_with_sso";
  const { emailRegex } = useGlobalConfig();
  const { t } = useTranslation();
  const { phone } = useBreakpoints();
  const { state, search }: LocationType = useLocation();
  const history = useHistory();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const intake = searchParams?.get("intake") === "true";
  const guestUuid = searchParams?.get("guest_uuid");
  const requestedPractitionerId = searchParams?.get("requestedPractitionerId");
  const referrer = searchParams.get("referrer");

  const patientEmailFromUrl = searchParams?.get("patient_email");

  const [email, setEmail] = useState(patientEmailFromUrl ?? "");
  const [isEmailDirty, setIsEmailDirty] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);

  const { treatmentPlanSlug, uuid, labRecommendationId, storeSlug } = patientAttributes;
  const identitySlug = uuid || treatmentPlanSlug;

  const resendParams = () => {
    const params = new URLSearchParams();
    if (additionalAttributes?.redirectPath) {
      params.append("redirect_path", additionalAttributes.redirectPath);
    }
    if (intake) {
      params.append("intake", intake.toString());
    }
    if (storeId) {
      params.append("store_id", storeId);
    }
    if (isStorefronts && guestUuid) {
      params.append("guest_uuid", guestUuid);
    }

    return params.toString();
  };
  const [patientNextLoginStep, { loading: isPatientNextLoginStepLoading }] =
    usePatientNextLoginStepUnauthenticatedSignUpSignInMutation({
      onCompleted: data => {
        const nextStep = data?.auth?.patientNextLoginStep?.nextStep || undefined;

        // Store email in the current history's state before redirecting to
        // preserve email when going back
        history.replace(history.location, { email });

        switch (nextStep) {
          case SwiftFillSteps.MAGIC_LINK:
            return navigate(
              {
                pathname: authRoutes.continue_without_password,
                search: resendParams(),
              },
              { state: { source: "RX_SIGN_IN", email, page: "success" } }
            );
          case SwiftFillSteps.CREATE_ACCOUNT:
            return navigate(
              {
                pathname: authRoutes.welcomeSignUp(storeSlug),
                search,
              },
              { state: { source: "RX_SIGN_IN", email } }
            );
          case SwiftFillSteps.PASSWORD:
            return navigate(
              { pathname: authRoutes.welcomePassword(storeSlug, identitySlug), search },
              { state: { source: "RX_SIGN_IN", email } }
            );
        }
      },
    });

  useEffect(() => {
    setEmail(state?.email || email);
  }, [state]);

  const onEmailChange = e => {
    setEmail(e.target.value);
    setIsEmailDirty(true);
  };

  const addPractitionerId = () => {
    if (!requestedPractitionerId) return {};

    return { requestedPractitionerId };
  };

  const onEmailSubmit = (e: SyntheticEvent) => {
    e.preventDefault();

    gRecaptchaExecute(captchaToken => {
      patientNextLoginStep({
        variables: {
          input: {
            captchaToken,
            attributes: {
              email,
              storeId,
              intake,
              redirectPath: isStorefronts
                ? authRoutes.continueGuestCheckout(referrer, guestUuid)
                : (additionalAttributes?.redirectPath ?? undefined),
              ...addPractitionerId(),
            },
          },
        },
      });
    }, setRecaptchaError);
  };

  const isButtonDisabled =
    !isEmailValid(email, emailRegex) || isPatientNextLoginStepLoading || isPreview;

  return (
    <SignInWrapper css={isStorefronts ? styles.signInWrapperWidth : undefined}>
      <SSOErrorBox ssoErrors={ssoErrors} />
      {!!recaptchaError && <RecaptchaErrorBox />}
      {!!isPreview && <PreviewModeBanner />}
      <LoginOptions
        commonSSOProps={{
          signUpPage: SIGN_IN_PAGE,
          pageVersion: PAGE_VERSION,
          disabled: isPreview,
          treatmentPlanSlug,
          labRecommendationId,
          storeSlug,
          uuid,
          additionalAttributes: {
            ...additionalAttributes,
            redirectPath: isStorefronts
              ? authRoutes.continueGuestCheckout(referrer, guestUuid)
              : (additionalAttributes?.redirectPath ?? undefined),
          },
          buttonStyles: isStorefronts ? styles.buttonStyles : undefined,
        }}
        isStorefronts={isStorefronts}
        isFullWidthButtons={isFullWidthButtons}
      />
      <form onSubmit={onEmailSubmit} css={styles.form}>
        <SignInEmailInput
          value={email}
          isDirty={isEmailDirty}
          onChange={onEmailChange}
          required
          disabled={isPreview}
        />
        {!!isStorefronts && (
          <>
            <Spacer height="oneHalf" />
            <ValuePropsCard />
          </>
        )}
        {!!customAuthDisclaimer && <CustomDisclaimer disclaimer={customAuthDisclaimer} />}
        <Spacer height="oneHalf" />
        <Button
          onClick={onEmailSubmit}
          disabled={isButtonDisabled}
          isFullWidth={isFullWidthButtons || phone.lessThan}
        >
          {t(l.practitionerSignUp.ContinueWithEmail)}
        </Button>
      </form>
    </SignInWrapper>
  );
};

export { EmailCheckForm };
